import { dateTimeRecordToLocalisedDateTime, makeDateTimeRecord } from "@visioni/schedules";
import { isExceptionAllDayRange } from "@src/features/schedules/utils/schedule-utils";
import { format } from "date-fns";
import type { AlarmException, AlarmExceptionSuggestion } from "@src/services/api/types";

export const toLocalDate = (record: AlarmException["period"]["start"], timezone: string): Date =>
  dateTimeRecordToLocalisedDateTime(record, timezone).toJSDate();

export const makeGroupKey = (ex: AlarmException): string =>
  `${ex.reason}|${ex.action}|${ex.period.start.year}-${ex.period.start.month}-${ex.period.start.day}-${ex.period.start.hour}-${ex.period.start.minute}|${ex.period.end.year}-${ex.period.end.month}-${ex.period.end.day}-${ex.period.end.hour}-${ex.period.end.minute}`;

export function formatExceptionDate(period: AlarmException["period"], timezone: string): string {
  const startDate = toLocalDate(period.start, timezone);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: timezone,
  }).format(startDate);
}

export function formatExceptionTime(period: AlarmException["period"], timezone: string): string {
  if (isExceptionAllDayRange(period.start, period.end)) {
    return "All day";
  }
  const startDate = toLocalDate(period.start, timezone);
  const endDate = toLocalDate(period.end, timezone);
  const timeFormatter = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: timezone });
  return `${timeFormatter.format(startDate)} - ${timeFormatter.format(endDate)}`;
}

const suggestionToDate = (date: AlarmExceptionSuggestion["date"]): Date =>
  new Date(date.year, date.month - 1, date.day);

// Shared formatter for AlarmExceptionSuggestion cards. The suggestion schema only
// carries `isAllDay` — there is no sub-day time range — so non-all-day suggestions
// intentionally return `time: null` and the UI hides the clock row.
export function formatSuggestionDate(suggestion: AlarmExceptionSuggestion): { date: string; time: string | null } {
  const dateStr = format(suggestionToDate(suggestion.date), "dd MMM yyyy");
  return {
    date: dateStr,
    time: suggestion.isAllDay ? "All day" : null,
  };
}

// Builds the exception period for a selected suggestion. The suggestion has only
// a date and an isAllDay flag, so the period spans the whole day regardless —
// the user can narrow the time range on the details screen.
export function buildPeriodFromSuggestion(suggestion: AlarmExceptionSuggestion): AlarmException["period"] {
  const { year, month, day } = suggestion.date;
  return {
    start: makeDateTimeRecord(year, month, day, 0, 0),
    end: makeDateTimeRecord(year, month, day, 24, 0),
  };
}
